import React, { createContext, useContext, useEffect, useState } from 'react';
import { EntitySortOrder, EntityType } from '../lib/enums';
import {
  readPersonaSortOrder,
  writePersonaSortOrder,
} from '../lib/utils/persona-sort-order-storage';

interface GlobalContextValue {
  hiddenExamples: EntityType[];
  toggleHiddenExample: (type: EntityType) => void;
  personaSortOrder: EntitySortOrder;
  setPersonaSortOrder: (order: EntitySortOrder) => void;
}

const GlobalContext = createContext<GlobalContextValue | undefined>(undefined);

const DEFAULT_HIDDEN_EXAMPLES: EntityType[] = [];

export const GlobalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [hiddenExamples, setHiddenExamples] = useState<EntityType[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_HIDDEN_EXAMPLES;
    try {
      const stored = localStorage.getItem('hiddenExamples');
      return stored ? JSON.parse(stored) : DEFAULT_HIDDEN_EXAMPLES;
    } catch {
      return DEFAULT_HIDDEN_EXAMPLES;
    }
  });

  useEffect(() => {
    localStorage.setItem('hiddenExamples', JSON.stringify(hiddenExamples));
  }, [hiddenExamples]);

  const toggleHiddenExample = (type: EntityType) => {
    setHiddenExamples((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const [personaSortOrder, setPersonaSortOrder] =
    useState<EntitySortOrder>(readPersonaSortOrder);

  useEffect(() => {
    writePersonaSortOrder(personaSortOrder);
  }, [personaSortOrder]);

  return (
    <GlobalContext.Provider
      value={{
        hiddenExamples,
        toggleHiddenExample,
        personaSortOrder,
        setPersonaSortOrder,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) throw new Error('useGlobal must be used within GlobalProvider');
  return context;
};
